/* ----- cart.js ----- */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

/**
 * Returns a space-joined concatenation of every selected vehicle option value.
 * e.g. "2026 Ram 1500 L6 3.0L Performance Exhaust RHO"
 */
window.getFullVehicleDetailsOnATC = () => {
  const vehicleData = localStorage.getItem('vehicleData');
  if (!vehicleData) return '';
  return JSON.parse(vehicleData)?.options?.map(option => option.value).join(' ') ?? '';
};

/**
 * Returns a vehicle title that mirrors the PDP grouping format.
 * e.g. "2003 Jeep Wrangler | L6 4.0L"
 */
window.getVehicleTitleOnATC = () => {
  const raw = localStorage.getItem('vehicleData');
  if (!raw) return '';

  const options = JSON.parse(raw)?.options ?? [];
  const getValue = (key) => options.find(o => o.key === key)?.value ?? null;

  const vehicleDetailGroups = [
    ['year', 'make', 'model', 'sub_model'],
    ['engine_base', 'engine_fuel_type'],
    ['bed_length'],
    ['body_type'],
  ];

  let vehicleConcatTitle = '';
  for (const group of vehicleDetailGroups) {
    const groupValues = group
      .map((key) => {
        let value = getValue(key);
        if (!value) return null;
        if (key === 'bed_length') value = value + ' Bed';
        if (key === 'body_type') value = value.replace(' Pickup', '');
        return value;
      })
      .filter(Boolean)
      .join(' ');

    if (groupValues) vehicleConcatTitle += groupValues + ' | ';
  }

  // Remove trailing pipe and whitespace
  return vehicleConcatTitle.trimEnd().replace(/\|$/, '').trimEnd();
};

window.cartUpdateVehicleDataAttr = () => {
  const cartAttributes = {
    "vehicleData": localStorage.getItem('vehicleData') || '[]'
  };

  fetch(window.Shopify.routes.root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      attributes: cartAttributes
    })
  })
  .then(response => response.json())
  .then(cart => {
  })
  .catch(error => {
    console.error('Error updating cart attributes:', error);
  });
};

/**
 * Updates cart line item props with current vehicle details for all unverified entries.
 * Does not gate on whether the entry's ID appears in verifiedIds.
 */
window.updateCartVehiclePropsOnVehicleSet = async ({ verifiedIds } = {}) => {
  const rawVehicleOnATC = localStorage.getItem('vehicleOnATC');
  if (!rawVehicleOnATC) return;

  let vehicleOnATC;
  try {
    vehicleOnATC = JSON.parse(rawVehicleOnATC);
  } catch (e) {
    console.error('Failed to parse vehicleOnATC:', e);
    return;
  }
  if (!vehicleOnATC.length) return;

  const unverifiedEntries = vehicleOnATC.filter(
    (vehicle) => !vehicle.verifiedIds || vehicle.verifiedIds.length === 0
  );
  if (!unverifiedEntries.length) return;

  const currentVerifiedIds = (verifiedIds && verifiedIds.length) ? verifiedIds : window.verifiedIds;
  if (!currentVerifiedIds || !currentVerifiedIds.length) return;

  const fullVehicleDetails = window.getFullVehicleDetailsOnATC();
  const vehicleConcatTitle = window.getVehicleTitleOnATC();

  const updates = [];

  for (const entry of unverifiedEntries) {
    updates.push({
      id: entry.key,
      properties: {
        _full_vehicle_details_on_atc: fullVehicleDetails,
        _vehicle_title_on_atc: vehicleConcatTitle,
      },
    });

    entry.verifiedIds = [...currentVerifiedIds];
  }

  if (!updates.length) return;

  try {
    for (const payload of updates) {
      await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    }
  } catch (e) {
    console.error('Failed to patch cart item(s):', e);
    return;
  }

  localStorage.setItem('vehicleOnATC', JSON.stringify(vehicleOnATC));
};

subscribe(PUB_SUB_EVENTS.vehicleData, async (event) => {
  window.cartUpdateVehicleDataAttr();
  await window.updateCartVehiclePropsOnVehicleSet({ verifiedIds: event?.verifiedIds });
  fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
    .then(res => res.json())
    .then(cart => {
      window.cartObj = cart;
      updateCartVerifiedFits();
    })
    .catch(e => console.error('Failed to fetch cart for updateCartVerifiedFits:', e));
});

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {}

  disconnectedCallback() {}

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#cartDrawerInner',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const sectionEL = document.getElementById(section.id);
          if (!sectionEL) return;

          const elementToReplace = sectionEL.querySelector(section.selector) || sectionEL;
          if (!elementToReplace) return;

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        if (window.affirm) affirm.ui.refresh();

        if (document.querySelector('#cartDrawerInner [data-swiper]')) {
          if (typeof window.loadSwiper === 'function') {
            window.loadSwiper();
          } else if (typeof window.swipers === 'function') {
            window.swipers();
          }
          setTimeout(() => {
            window.removeCartItemsFromUpsell(parsedState);
          }, 100);
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';

        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('{{quantity}}', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(error => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (!errors) return;
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError) {
      if (typeof message !== 'undefined' && message !== '') lineItemError.removeAttribute('hidden');
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (!(html || selector)) return;
    const $selector = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!$selector) return;
    return $selector.innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    mainCartItems.classList.add('pointer-events');

    const cartItems = this.querySelectorAll(`#CartItem-${line}`);
    cartItems.forEach((item) => item.classList.add('loading'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    mainCartItems?.classList.remove('pointer-events');

    const cartItems = this.querySelectorAll(`#CartItem-${line}`);
    cartItems.forEach((item) => item.classList.remove('loading'));
    document.dispatchEvent(new CustomEvent('CartItemsOnChange'));
    document.dispatchEvent(new CustomEvent('cart-disable-loading'));
  }
};

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/* ----- quantity-popover.js ----- */
if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 990px)');
        this.mqlTablet = window.matchMedia('(min-width: 750px)');
        this.infoButtonDesktop = this.querySelector('.quantity-popover__info-button--icon-only');
        this.infoButtonMobile = this.querySelector('.quantity-popover__info-button--icon-with-label');
        this.popoverInfo = this.querySelector('.quantity-popover__info');
        this.closeButton = this.querySelector('.button-close');
        this.variantInfo = this.querySelector('.quantity-popover-container');
        this.eventMouseEnterHappened = false;

        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.closePopover.bind(this));
        }

        if (this.popoverInfo && this.infoButtonDesktop && this.mql.matches) {
          this.popoverInfo.addEventListener('mouseenter', this.closePopover.bind(this));
        }

        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener('click', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('focusout', this.closePopover.bind(this));
        }

        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener('click', this.togglePopover.bind(this));
          this.infoButtonMobile.addEventListener('focusout', this.closePopover.bind(this));
        }

        if (this.infoButtonDesktop && this.mqlTablet.matches) {
          this.variantInfo.addEventListener('mouseenter', this.togglePopover.bind(this));
          this.variantInfo.addEventListener('mouseleave', this.closePopover.bind(this));
        }
      }

      togglePopover(event) {
        event.preventDefault();
        if (event.type === 'mouseenter') {
          this.eventMouseEnterHappened = true;
        }

        if (event.type === 'click' && this.eventMouseEnterHappened) return;

        const button = this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        button.setAttribute('aria-expanded', !isExpanded);

        this.popoverInfo.toggleAttribute('hidden');

        const isOpen = button.getAttribute('aria-expanded') === 'true';

        button.classList.toggle('quantity-popover__info-button--open');

        if (isOpen && event.type !== 'mouseenter') {
          button.focus();
        }
      }

      closePopover(event) {
        event.preventDefault();
        const isChild = this.variantInfo.contains(event.relatedTarget);

        const button = this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;

        if (!event.relatedTarget || !isChild) {
          button.setAttribute('aria-expanded', 'false');
          button.classList.remove('quantity-popover__info-button--open');
          this.popoverInfo.setAttribute('hidden', '');
        }

        this.eventMouseEnterHappened = false;
      }
    }
  );
}

/* ---- cart-drawer.js ----- */
class CartDrawer extends HTMLElement {
  constructor() {
    if (window.CartDrawerInit) return;

    super();
    window.addEventListener('keyup', (evt) => evt.code === 'Escape' && document.documentElement.classList.contains('cart-drawer--active') && this.close());
    this.querySelector('#cartDrawerOverlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    window.CartDrawerInit = true;
  }

  setHeaderCartIconAccessibility() {
    if (window.location.pathname === '/cart') return;

    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    /* cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    }); */
  }

  open(triggeredBy) {
    document.dispatchEvent(new CustomEvent('cart-drawer-opened'));

    cartDrawerInner.scroll(0, 0);

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    requestAnimationFrame(() => {
      lockScrollEnable();
      document.documentElement.classList.add('cart-drawer--active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        window.updateCartCount();

        const message = document.querySelector('.catalytic-converter-message');
        if (message && sessionStorage.getItem('catalyticConverterMessageDismissed') !== 'true') {
          message.style.display = 'flex';
        }
      },
      { once: true }
    );
  }

  close() {
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('cart-drawer--active');
    lockScrollDisable();
    window.updateCartCount();
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart-drawer-closed'));
    }, 250);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#cartDrawerOverlay').addEventListener('click', this.close.bind(this));
      this.open();

      if (window.affirm) affirm.ui.refresh();

      if (document.querySelector('#cartDrawerInner [data-swiper]')) {
        if (typeof window.loadSwiper === 'function') {
          window.loadSwiper();
        } else if (typeof window.swipers === 'function') {
          window.swipers();
        }
        setTimeout(() => {
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => window.removeCartItemsFromUpsell(cart))
            .catch(error => console.error('Error fetching cart:', error));
        }, 100);
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#cartDrawerInner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.aftershipHidden = false;
    this.limitedUpdate = this.hasAttribute('data-limited-update') && this.dataset.limitedUpdate === 'true';
  }

  connectedCallback() {
    this.swatchChangeUnsubscriber = window.subscribe(PUB_SUB_EVENTS.swatchChange, event => {
      const selectedEl = this.querySelector('input:checked');
      const selectedV = event.data.variants.find(v => v.title === selectedEl.value);
      this.currentVariant = selectedV;
      this.dataset.url = event.data.url;

      window.publish(PUB_SUB_EVENTS.variantChange, {
        sectionId: this.dataset.section,
        currentVariant: this.currentVariant,
      });
    });
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateAffirm();

    if (!this.limitedUpdate) {
      this.toggleAddButton(true, '', false);
    }

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateOptionsLegend();
      this.updateVariantInput();
      this.renderProductInfo();

      if (!this.limitedUpdate) {
        this.updateSku();
        this.updateURL();
      } else {
        this.updateSetInputs();
      }
    }
  }

  updateAffirm() {
    const affirmWidgets = document.querySelectorAll('[data-product-affirm]');
    affirmWidgets.forEach(affirmWidget => {
      affirmWidget.setAttribute('data-amount', this.currentVariant.price);
    });
    affirm.ui.refresh();
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateOptionsLegend() {
    this.currentVariant.options.forEach((option, index) => {
      const replaceText = this.getOptionNameReplaceText() && this.getOptionNameReplaceText().find(o => option === o.title);
      const el = this.querySelector(`[data-product-option-active="${index}"]`);
      el ? el.innerText = replaceText ? replaceText.replace : option : null;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });

    window.publish(PUB_SUB_EVENTS.variantChange, {
      sectionId: this.dataset.section,
      currentVariant: this.currentVariant,
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => { parent.scroll(0, 0) });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateSku() {
    if (!(this.currentVariant && document.getElementById('productSku'))) return;
    productSku.innerText = 'SKU: ' + this.currentVariant.sku;
    if (!(this.currentVariant && document.getElementById('productStickySku'))) return;
    productStickySku.innerText = 'SKU: ' + this.currentVariant.sku;
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment, #product-form-sticky`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateStickyATC(html) {
    const mediaDestination = document.getElementById('productStickyMedia');
    const mediaSource = html.getElementById('productStickyMedia');
    const priceDestination = document.getElementById('productStickyPrice');
    const priceSource = html.getElementById('productStickyPrice');
    const titleDestination = document.getElementById('pdpStickyTitle');
    const titleSource = html.getElementById('pdpStickyTitle');
    const yotpoDestination = document.getElementById('productStickyYotpo');
    const yotpoSource = html.getElementById('productStickyYotpo');

    if (mediaSource && mediaDestination) mediaDestination.innerHTML = mediaSource.innerHTML;
    if (priceSource && priceDestination) priceDestination.innerHTML = priceSource.innerHTML;
    if (titleSource && titleDestination) titleDestination.innerHTML = titleSource.innerHTML;
    if (yotpoSource && yotpoDestination) yotpoDestination.setAttribute('data-yotpo-product-id', yotpoSource.getAttribute('data-yotpo-product-id'));

    document.getElementById('productStickyPrice')?.classList.remove('visibility-hidden');
  }

  renderProductInfo() {
    const sectionId = this.dataset.section;
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${sectionId}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(id);
        const yotpoDestination = document.getElementById('productYotpo');
        const yotpoSource = html.getElementById('productYotpo');

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (yotpoSource && yotpoDestination) yotpoDestination.setAttribute('data-yotpo-product-id', yotpoSource.getAttribute('data-yotpo-product-id'));

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('visibility-hidden');
        const oosText = window.variantStrings?.soldOut || 'Sold out';
        const availableText = window.variantStrings?.addToCart || 'Add to cart';
        this.toggleAddButton(!this.currentVariant.available, this.currentVariant.available ? availableText : oosText);
        this.updateStickyATC(html);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButtons = document.querySelectorAll(`#product-form-${this.dataset.section} [name="add"], #product-form-sticky [name="add"]`);

    if (!addButtons.length) return;

    addButtons.forEach((addButton) => {
      if (text) addButton.textContent = text;

      if (disable) {
        addButton.setAttribute('disabled', true);
        if (this.currentVariant && !this.currentVariant.available) {
          document.dispatchEvent(new CustomEvent('restyleAftershipLoadingStart'));
          this.aftershipHidden = true;
        }
      } else {
        addButton.removeAttribute('disabled');
        if (this.aftershipHidden) document.dispatchEvent(new CustomEvent('restyleAftership'));
      }
    });

    if (!modifyClass) return;
  }

  setUnavailable() {
    const unavailableText = window.variantStrings?.unavailable || 'Unavailable';
    this.toggleAddButton(true, unavailableText, false);
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"][data-json="product-variants"]').textContent);
    return this.variantData;
  }

  getOptionNameReplaceText() {
    this.optionNameReplaceText = this.optionNameReplaceText || JSON.parse(this.querySelector('[type="application/json"][data-json="option-name-replace-text"]').textContent);
    return this.optionNameReplaceText;
  }

  updateSetInputs() {
    this.querySelectorAll(':checked').forEach(input => {
      const setInput = document.querySelector(`input[data-form-id="${input.getAttribute('form')}"][name$="id"]`);
      if (setInput) setInput.value = this.currentVariant.id;
    });
  }
};

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const inputs = Array.from(fieldset.querySelectorAll('input'));
      return inputs.length === 1 ? inputs[0].value : inputs.length && inputs.find((radio) => radio.checked).value;
    });
  }
};

customElements.define('variant-radios', VariantRadios);

/* ----- globals ----- */
window.updateProductCards = () => {
  document.querySelectorAll('[data-product-card]').forEach(el => {
    const storedProduct = window.productInStorage(el.dataset.handle);
    const isVerified = storedProduct && typeof storedProduct !== 'undefined';
    el.dataset.isVerified = isVerified;
    el.classList.add(isVerified ? 'is-verified-fit' : 'not-verified-fit');
    el.classList.remove(isVerified ? 'not-verified-fit' : 'is-verified-fit');
    const fitmentEl = el.querySelector('fitment-notes:not(:has(li))');
    if (storedProduct && storedProduct.fitmentNotes && fitmentEl) fitmentEl.innerHTML = fitmentEl.innerHTML.replace('{{fitmentNotes}}', window.renderFitmentNotes(storedProduct.fitmentNotes));
  });
};

(() => {
  let updateQueued = false;

  const scheduleProductCardsUpdate = () => {
    if (updateQueued) return;
    updateQueued = true;
    requestAnimationFrame(() => {
      window.updateProductCards();
      updateQueued = false;
    });
  };

  const observeProductCards = () => {
    const observer = new MutationObserver(mutations => {
      const hasNewCards = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType !== 1) return false;
          return node.matches('[data-product-card]') || !!node.querySelector('[data-product-card]');
        })
      );
      if (hasNewCards) scheduleProductCardsUpdate();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) {
    observeProductCards();
  } else {
    document.addEventListener('DOMContentLoaded', observeProductCards);
  }
})();

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function lockScrollEnable() {
  window.pageY = window.scrollY;
  document.body.style.left = 0;
  document.body.style.right = 'var(--scrollbar-width)';
  document.body.style.top = `-${window.pageY}px`;
  document.body.style.position = 'fixed';
}

function lockScrollDisable() {
  document.body.style.position = null;
  document.body.style.left = null;
  document.body.style.right = null;
  document.body.style.top = null;
  window.scrollTo(0, window.pageY);
}

function updateCartVerifiedFits(obj) {
  window.cartObj = typeof obj !== 'undefined' ? obj : window.cartObj || {};
  const hasItems = cartObj.hasOwnProperty('items') && cartObj.items.length > 0;

  if (hasItems) {
    const storedVehicles = JSON.parse(localStorage.getItem('vehicleOnATC') || '[]');
    const cartEls = document.querySelectorAll('.cart-item');

    cartObj.items.forEach((item, index) => {
      const cartEl = cartEls[index];
      const productGid = `gid://shopify/Product/${item.product_id}`;
      const vehicleFitTagline = cartEl?.querySelector('[data-fit-vehicle-tagline]');
      const storedVehicle = storedVehicles.find(vehicle => String(vehicle.id) === String(item.product_id));

      let isVerified = false;

      if (storedVehicle && storedVehicle.verifiedIds.length && storedVehicle.verifiedIds.includes(productGid)) {
        isVerified = true;
      }

      if (isVerified) {
        // Verified Fit
        cartEl?.classList.add('is-verified-fit');
        if (vehicleFitTagline) vehicleFitTagline.innerHTML = `Fits Your ${item.properties['_vehicle_title_on_atc'] || 'Vehicle'}`;
      } else if (storedVehicle?.verifiedIds?.length) {
        // Does Not Fit
        cartEl?.classList.add('not-verified-fit');
      if (vehicleFitTagline) vehicleFitTagline.innerHTML = `Does Not Fit Your ${item.properties['_vehicle_title_on_atc'] || 'Vehicle'}`;
      } else {
        // Verify Fitment
        cartEl?.classList.remove('is-verified-fit', 'not-verified-fit');
        if (vehicleFitTagline) vehicleFitTagline.style.display = 'none';
    }
    });
  }
}

subscribe(PUB_SUB_EVENTS.cartUpdate, e => {
  if (e.source === 'product-form') return;
  cartObj = e.cartData;
  updateCartVerifiedFits();
});

function loadMinicart() {
  fetch(`${window.Shopify.routes.root}?sections=cart-drawer`)
    .then(response => response.json())
    .then(data => {
      document.querySelector('#cartDrawerInner').innerHTML = (new DOMParser().parseFromString(data['cart-drawer'], 'text/html').querySelector('#cartDrawerInner').innerHTML);

      if (window.affirm) affirm.ui.refresh();

      const hasUpsells = document.querySelector('#cartDrawerInner [data-swiper]') !== null;

      if (hasUpsells) {
        if (typeof window.loadSwiper === 'function') {
          window.loadSwiper();
        } else if (typeof window.swipers === 'function') {
          window.swipers();
        }
      }

      setTimeout(() => {
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            window.cartObj = cart;
            updateCartVerifiedFits();
            if (hasUpsells) window.removeCartItemsFromUpsell(cart);
          })
          .catch(error => console.error('Error fetching cart:', error));
      }, 100);
    })
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; /* If not TAB key */
    /* On the last focusable element and tab forward, focus the first element. */
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    /*  On the first focusable element and tab backward, focus the last element. */
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        if (!this.form) return;

        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Capture selected vehicle data on ATC
        // To separate line items by selected vehicle and provide clarity to CS on order
        formData.append('properties[_full_vehicle_details_on_atc]', window.getFullVehicleDetailsOnATC());
        formData.append('properties[_vehicle_title_on_atc]', window.getVehicleTitleOnATC());

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton && this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              fetch(`${routes.cart_url}.js`)
                .then(response => response.json())
                .then(data => {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form-cart-total',
                    cartData: data,
                  });
                });

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            this.cart.renderContents(response);

            // Store vehicle data on ATC locally
            const id = response.product_id;
            const key = response.key;
            const verifiedIds = window.verifiedIds || [];
            const existing = localStorage.getItem('vehicleOnATC');
            const vehicleOnATC = existing ? [...JSON.parse(existing), { id, key, verifiedIds }] : [{ id, key, verifiedIds }];

            localStorage.setItem('vehicleOnATC', JSON.stringify(vehicleOnATC));
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            this.closest('.quickadd--loading')?.classList.remove('quickadd--loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

(() => {
  const onScroll = () => {
    if (window.menuToggling) return;


    if (window.pageYOffset > window.headerScrolledThreshold) {
      document.documentElement.classList.add('header--scrolled');
    } else {
      document.documentElement.classList.remove('header--scrolled');
    }

    if (window.pageYOffset > 300 + window.headerScrolledThreshold) {
      document.documentElement.classList.add('header--scrolled--after');
    } else {
      document.documentElement.classList.remove('header--scrolled--after');
    }

    const scrollTop = window.pageYOffset;

    /* scroll direction */
    window.scrollDirection = scrollTop > lastScrollTop ? 'down' : 'up';
    document.documentElement.dataset.scrollDirection = window.scrollDirection;
    lastScrollTop = window.pageYOffset;
  };

  const inViewVideo = (el, src) => {
    const $video = el.closest('video');

    if (!$video) return;
    if (typeof src !== 'undefined') $video.src = el.dataset.inViewVideoSrc;

    if (!$video.hasAttribute('data-autoplay')) return;
    /* $video.removeAttribute('controls'); */
    $video.play();
  };

  window.lazyLoadScript = obj => {
    const isLoaded = document.head.querySelector(`[data-in-view-script-name='${obj.src}']`);
    const cb = obj.callback && typeof window[obj.callback] === 'function' ? window[obj.callback] : null;

    if (isLoaded) {
      if (cb) cb();
      return;
    }

    if (!obj.hasOwnProperty('src')) {
      // only need to fire the callback, no script to load
      if (cb) cb();
      return;
    }

    const el = document.createElement('script');
    el.dataset.inViewScriptName = obj.src;
    el.type = obj.type || 'text/javascript';
    if (obj.defer) el.defer = true;
    else if (obj.async) el.async = true;
    el.src = obj.src;
    document.head.appendChild(el);

    if (cb) el.onload = requestAnimationFrame(() => {
      cb()
    });
  }

  const inViewScript = obj => {
    lazyLoadScript(obj);
  };

  window.loadSwiper = () => {
    /* load swiper if needed */
    if (!window.swiperLoaded && typeof Swiper !== 'function') {
      const $script = document.createElement('script');
      $script.async = true;
      $script.src = window.assetUrls['swiper.js'];
      document.head.appendChild($script);
      window.swiperLoaded = true
    }

    const swiperIsReady = new Promise(resolve => {
      let timer = setInterval(() => {
        if (typeof Swiper === 'function') {
          clearInterval(timer);
          return resolve();
        }
      }, 30)
    });

    swiperIsReady.then(() => {
      window.swipers();
    }).catch(error => {
      /* console.log('Something went wrong', error); */
    })
  };

  window.buildQuickAdd = (el, variants)  => {
    const quickaddEl = el.querySelector('[data-product-card-quickadd]');
    if (!quickaddEl) return;

    quickaddEl.innerHTML = '';
    let els = [];
    variants.forEach(variant => {
      const button = document.createElement('button');
      const span = document.createElement('span');
      const title = variant.available ? `Add ${variant.name} to cart` : `${variant.name} is out of stock`;
      button.type = 'button';
      if (!variant.available) button.setAttribute('disabled', 'disabled');
      button.classList = 'product-card__quickadd-btn';
      button.setAttribute('data-quickadd-variant', '');
      button.setAttribute('aria-label', title);
      button.setAttribute('title', title);
      button.setAttribute('onclick', `window.onQuickAdd(event.target, ${variant.id})`);
      span.setAttribute('aria-hidden', true);
      span.innerText = variant.title.split('/ ')[1].replace(/&#39;/g , "\'");
      button.appendChild(span);
      els.push(button);
      quickaddEl.appendChild(button);
    });
  };

  window.onQuickAdd = (el, variant) => {
    el.closest('.product-card').classList.add('quickadd--loading');
    const $form = el.closest('form');
    const id = $form.querySelector('[name="id"]');
    id.value = variant;
    el.closest('form').dispatchEvent(new Event('submit', { bubbles: true }));
    /* window.addToCart($form); */
  };

  /* product-card: update card on swatch click */
  window.onSwatchClick = e  => {
    if (e.target.classList.contains('swatches__item--active')) return;

    e.preventDefault();

    const alreadyClicked = e.target.classList.contains('clicked');
    const data = JSON.parse(e.target.dataset.swatch);
    let img;

    /* make request for image */
    if (!alreadyClicked) {
      img = new Image();
      img.src = data.images[0].src;
      e.target.classList.add('clicked');
    }

    const $card = e.target.closest('[data-product-card]');

    /* build swatches */
    buildQuickAdd($card, data.variants);

    /* loading state: begin */
    $card.classList.add('product-card--loading');

    /* swatch active class: remove previous */
    $card.querySelector('.swatches__item--active')?.classList.remove('swatches__item--active');

    /* swatch active class: add to clicked swatch */
    e.target.classList.add('swatches__item--active');

    /*
      update card via swatch data
    */

    /* update card: image */
    $card.querySelectorAll('[data-product-card-image]').forEach((el, index) => {
      const $src = el.querySelector('[data-product-card-image-src]');
      const $srcset = el.querySelector('[data-product-card-image-srcset]');
      $src.alt = data.images[index].alt;
      $src.src = data.images[index].src;
      $src.srcset = data.images[index].srcset;
      $srcset.srcset = data.images[index].srcsetLarge;
    });

    /* update card: url */
    const $url = $card.querySelector('[data-product-card-url]');
    $url.href = data.url;

    /* update card: url title attribute */
    $url.title = `View ${data.title} in ${data.variantName} product details`;

    /* update card: price */
    if (data.priceCompareAtAmount > data.priceAmount) {
      $card.querySelector('[data-product-card-price-compare]').innerHTML = data.priceCompareAt;
      $card.querySelector('[data-product-card-price-regular]').innerHTML = data.price;
    } else {
      $card.querySelector('[data-product-card-price-compare]').innerHTML = '';
      $card.querySelector('[data-product-card-price-regular]').innerHTML = data.price;
    }

    /* update card: title */
    $card.querySelector('[data-product-card-title]').innerText = data.title.replace(/&lt;br&gt;/g, "\n").replace(/&#39;/g , "\'");

    /* update card: variant name */
    $card.querySelector('[data-product-card-variant-name]').innerText = data.variantName;

    /* loading state: end */
    if (alreadyClicked) {
      setTimeout(() => {
        $card.classList.remove('product-card--loading');
      }, 200);
    } else {
      img.onload = () => {
        setTimeout(() => {
          $card.classList.remove('product-card--loading');
        }, 200);
      }
    }
  };

  window.publishProductsData = data => {
    /* USAGE:
      window.addEventListener('publishProductsData', data => {
        console.log('<gtm-helper> publishProductsData listener', data.detail);
      });
    */
    window.dispatchEvent(new CustomEvent('publishProductsData', {
      detail: data,
    }));
  };

  /* swipers init via data-swiper={options} */
  window.swiperEls = [];
  window.swipers = () => {
    document.querySelectorAll('[data-swiper]:not(.swiper-initialized)').forEach((el, index) => {
      window.swiperEls[index] = new Swiper(el, Object.assign({}, {
        a11y: true,
        observer: true,
        slidesPerView: 'auto',
        threshold: 8,
      }, JSON.parse(el.dataset.swiper)));

      const changeEvent = el.getAttribute('data-swiper-slide-change');

      if (changeEvent && window.hasOwnProperty(changeEvent) && typeof window[changeEvent] === 'function') {
        window.swiperEls[index].on('slideChange', function(e) {
          window[changeEvent](e);
        })
      }
    });
  };

  window.inView = () => {
    /* add .in-view class to [data-in-view] els as they enter the viewport */
    const inViewEls = document.querySelectorAll('[data-in-view]');

    const inViewObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');

          if (entry.target.hasAttribute('data-autoplay') || entry.target.hasAttribute('data-in-view-video-src')) {
            inViewVideo(entry.target, entry.target.dataset.inViewVideoSrc);
          }

          if (entry.target.hasAttribute('data-in-view-swiper')) {
            loadSwiper();
            inViewObserver.unobserve(entry.target);
          }

          if (entry.target.hasAttribute('data-in-view-script')) {
            inViewScript(JSON.parse(entry.target.dataset.inViewScript));
          }
        } else {
          if (entry.target.hasAttribute('data-autoplay') && entry.target.paused == false) {
            entry.target.pause()
          }
        }
      });
    });

    inViewEls.forEach(el => inViewObserver.observe(el));
  };

  document.addEventListener('DOMContentLoaded', () => {
    window.updateProductCards();
    customElements.define('cart-remove-button', CartRemoveButton);
    customElements.define('cart-items', CartItems);
    customElements.define('cart-drawer', CartDrawer);

    window.lastScrollTop = window.pageYOffset;

    onScroll();
    inView();

    window.addEventListener('scroll', () => {
      onScroll();
    }, { passive: true });
  });

  window.addEventListener('keyup', e => {
    if (e.key === 'Escape') {
      document.dispatchEvent(new CustomEvent('onEsc'));
      const expandedCards = document.querySelectorAll('[data-product-card].show-quickadd');
      expandedCards.forEach(card => {
        card.classList.remove('show-quickadd');
        const actionBtn = card.querySelector('[data-quickadd-action-btn]');
        if (actionBtn) {
          actionBtn.setAttribute('aria-expanded', 'false');
          actionBtn.focus();
        }
      });
    }
  });

  window.updateCartCount = (delay = 300) => {
    requestAnimationFrame(() => {
      const $count = document.querySelector('[data-cart-icon-bubble-count]');
      const count = $count ? $count.innerText : 1
      /* wait for cart-drawer transition to finish then update data-cart-count which is used to adjust the padding next to cart icon in header*/
      setTimeout(() => {
        document.documentElement.dataset.cartCount = count
      }, delay);
    });
  }

  document.addEventListener('CartItemsOnChange', () => {
    window.updateCartCount();
  })

  window.removeCartItemsFromUpsell = (cart) => {
    if (!cart || !cart.items) return;

    const cartProductIds = cart.items.map(item => item.product_id);
    const upsellCarousel = document.querySelector('#cartDrawerInner [data-swiper][data-upsell-carousel]');

    if (!upsellCarousel) return;

    const swiperInstance = window.swiperEls.find(swiper =>
      swiper && swiper.el === upsellCarousel
    );

    if (!swiperInstance) return;

    const slides = Array.from(upsellCarousel.querySelectorAll('.swiper-slide[data-upsell-product-id]'));

    for (let i = slides.length - 1; i >= 0; i--) {
      const slide = slides[i];
      const productId = parseInt(slide.dataset.upsellProductId);

      if (cartProductIds.includes(productId)) {
        const slideIndex = Array.from(swiperInstance.slides).indexOf(slide);
        if (slideIndex !== -1) {
          swiperInstance.removeSlide(slideIndex);
        }
      }
    }
  };
})();